import type { Aggreg8Bank, BankAccount, BankAnalyses, InfoSharingConsent, UserFlow } from "@bankmonitor/customer-portal-rest-client";
import { AccountAnalysisStatusType, UserFlowStatusType } from "@bankmonitor/customer-portal-rest-client";
import dayjs from "dayjs";
import { accountRoleLabel } from "./Analysis.data";
import type { AnalysisAccount, Authorization, AuthorizationErrorType, AuthorizationStatus } from "./AnalysisService.type";

export type BankNameLookup = ReadonlyMap<string, string>;

export function bankNameLookup(banks: Aggreg8Bank[]): BankNameLookup {
	return new Map(banks.map((bank) => [bank.bmCode, bank.name]));
}

function toIsoDate(date: Date): string {
	return dayjs(date).format("YYYY-MM-DD");
}

function isValidDate(date: Date | undefined): date is Date {
	return date !== undefined && !Number.isNaN(date.getTime());
}

function toIsoDateSafe(date: Date | undefined): string | undefined {
	return isValidDate(date) ? toIsoDate(date) : undefined;
}

function earliestAccountPeriodFrom(accounts: BankAccount[]): Date | undefined {
	const froms = accounts.map((account) => account.period?.from).filter(isValidDate);
	return froms.length > 0 ? froms.reduce((earliest, current) => (current < earliest ? current : earliest)) : undefined;
}

function latestAccountPeriodTo(accounts: BankAccount[]): Date | undefined {
	const tos = accounts.map((account) => account.period?.to).filter(isValidDate);
	return tos.length > 0 ? tos.reduce((latest, current) => (current > latest ? current : latest)) : undefined;
}

function deriveStatus(consent: InfoSharingConsent): AuthorizationStatus {
	if (consent.deletedAt) {
		return "REVOKED";
	}
	if (isValidDate(consent.expiresAt) && consent.expiresAt.getTime() < Date.now()) {
		return "EXPIRED";
	}
	return "SYNCED";
}

export function infoSharingConsentToAuthorization(consent: InfoSharingConsent, bankNames?: BankNameLookup): Authorization {
	const bankCode = consent.bank?.bmCode ?? consent.aggreg8Id;

	return {
		id: String(consent.id),
		infoSharingConsentId: consent.id,
		bankCode,
		bankLabel: bankNames?.get(bankCode) ?? consent.bank?.name ?? consent.aggreg8Id,
		status: deriveStatus(consent),
		periodFrom: toIsoDateSafe(consent.createdAt),
		periodTo: toIsoDateSafe(consent.expiresAt),
		accounts: [],
	};
}

export function infoSharingConsentsToAuthorizations(consents: InfoSharingConsent[], bankNames?: BankNameLookup): Authorization[] {
	return consents.map((consent) => infoSharingConsentToAuthorization(consent, bankNames));
}

export function userFlowToAuthorization(userFlow: UserFlow, bankNames?: BankNameLookup): Authorization {
	const bankCode = userFlow.bank.bmCode;

	return {
		id: userFlow.userFlowId,
		bankCode,
		bankLabel: bankNames?.get(bankCode) ?? userFlow.bank.name,
		status: "PROCESSING",
		periodFrom: toIsoDateSafe(userFlow.createdAt),
		accounts: [],
	};
}

// Drop flows whose userFlowId already has a consent to avoid a duplicate card.
export function inProgressUserFlowsToAuthorizations(userFlows: UserFlow[], consents: InfoSharingConsent[], bankNames?: BankNameLookup): Authorization[] {
	const consentUserFlowIds = new Set(consents.map((consent) => consent.userFlowId).filter((id): id is string => id !== undefined));

	return userFlows
		.filter(
			(userFlow) => [UserFlowStatusType.Pending, UserFlowStatusType.InProgress].includes(userFlow.status) && !consentUserFlowIds.has(userFlow.userFlowId),
		)
		.map((userFlow) => userFlowToAuthorization(userFlow, bankNames));
}

function deriveAnalysisStatus(analysis: BankAnalyses): AuthorizationStatus {
	switch (analysis.status) {
		case AccountAnalysisStatusType.AnalysisDone:
			return isValidDate(analysis.validTo) && analysis.validTo.getTime() < Date.now() ? "EXPIRED" : "SYNCED";
		case AccountAnalysisStatusType.Aggreg8UserFlowFailed:
		case AccountAnalysisStatusType.AnalysisFailed:
		case AccountAnalysisStatusType.AnalysisFailedNoData:
			return "FAILED";
		case AccountAnalysisStatusType.Aggreg8UserFlowInProgress:
			// User flow started but not yet completed (no identifiers submitted); cancellable by the user.
			return "IN_PROGRESS";
		case AccountAnalysisStatusType.UserCancelled:
			return "CANCELLED";
		default:
			// Aggreg8UserFlowSuccess, AnalysisInProgress and a missing status
			return "PROCESSING";
	}
}

// Only meaningful once deriveAnalysisStatus has resolved to FAILED; the three raw failure codes it collapses into
// that one status are exactly the distinction BM_authorizationReturn's errorType needs.
function deriveErrorType(analysis: BankAnalyses): AuthorizationErrorType | undefined {
	switch (analysis.status) {
		case AccountAnalysisStatusType.Aggreg8UserFlowFailed:
			return "AGGREG8_USER_FLOW_FAILED";
		case AccountAnalysisStatusType.AnalysisFailed:
			return "ANALYSIS_FAILED";
		case AccountAnalysisStatusType.AnalysisFailedNoData:
			return "ANALYSIS_FAILED_NO_DATA";
		default:
			return undefined;
	}
}

function bankAccountToAnalysisAccount(account: BankAccount, hasResult: boolean, validityDate: string | undefined): AnalysisAccount {
	return {
		name: accountRoleLabel(account.accountRole, account.currency),
		number: account.accountNumber,
		currency: account.currency,
		observedCost: hasResult ? account.currentCost?.observedCost : undefined,
		annualCardFee: hasResult ? account.currentCost?.annualCardFee : undefined,
		yearlyCost: hasResult ? account.currentCost?.estimatedAnnualCost : undefined,
		validityDate,
		eligibleForFreeAccount: account.eligibleForFreeAccount,
	};
}

export function bankAnalysesToAuthorization(analysis: BankAnalyses, bankNames?: BankNameLookup): Authorization {
	const status = deriveAnalysisStatus(analysis);
	// Only settled analyses carry cost data; IN_PROGRESS/CANCELLED/PROCESSING/FAILED render empty, like a failed one.
	const hasResult = status === "SYNCED" || status === "EXPIRED";
	const validityDate = hasResult ? toIsoDateSafe(analysis.validTo) : undefined;

	return {
		id: analysis.analysisId !== undefined ? String(analysis.analysisId) : undefined,
		bankCode: analysis.bankCode,
		bankLabel: bankNames?.get(analysis.bankCode) ?? analysis.bankCode,
		status,
		errorType: deriveErrorType(analysis),
		periodFrom: toIsoDateSafe(earliestAccountPeriodFrom(analysis.accounts)),
		periodTo: toIsoDateSafe(latestAccountPeriodTo(analysis.accounts)),
		validityDate,
		analysisDate: hasResult ? toIsoDateSafe(analysis.createdAt) : undefined,
		totalAnnualBankCost: hasResult ? analysis.totalAnnualBankCost : undefined,
		totalObservedBankCost: hasResult ? analysis.totalObservedBankCost : undefined,
		accounts: analysis.accounts.map((account) => bankAccountToAnalysisAccount(account, hasResult, validityDate)),
	};
}

export function bankAnalysesToAuthorizations(analyses: BankAnalyses[], bankNames?: BankNameLookup): Authorization[] {
	return analyses.map((analysis) => bankAnalysesToAuthorization(analysis, bankNames));
}
